/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { UploadStatus } from '@msteams/services-io-browser-file-upload';

import { ApprovalRequestType } from '../../data/types.generated';
import { PanelRegion, PanelType, ScenarioName, StatusName, StepName, SubWorkLoad } from '../../logger';
import { LogCategory } from '../../logger';
import { AdobeFileAttachment } from '../../models/fileService/AdobeFileAttachment';
import { FileService } from '../../models/fileService/FileService';
import { newGuid } from '../../utilities/AppUtils';
import { getAppHost } from '../../utilities/TeamsUtils';
import { stringFormat } from '../../utilities/Utils';
import { uploadFileEsignGateway } from './UploadFileEsignGateway';

export const acceptedFileTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

export interface AdobeResponseBody {
    transientDocumentId?: string;
}

export class AdobeFileService implements FileService<AdobeFileAttachment> {
    private logger = getAppHost().getLogger();
    private loc = getAppHost().getLocale();

    public async uploadFile(
        file: File,
        notifier: (progress: number, status: UploadStatus) => void
    ): Promise<Partial<AdobeFileAttachment>> {
        this.logger.logTraceInfo(LogCategory.AdobeFileUpload, `Calling ${AdobeFileService.name} - uploadFile`);
        this.logger.extendScenario(ScenarioName.AdobeFileUpload, ScenarioName.CreateApprovals);

        const {
            adobe_upload_error_default,
            adobe_upload_error_invalid_types,
            adobe_upload_error_invalid_token,
            esign_upload_error_file_too_large,
            adobe_upload_error_canceled,
            adobe_upload_error_open_window_failed,
        } = this.loc.strings;

        const adobeUploadId = newGuid();
        this.logger.logTraceInfo(LogCategory.AdobeFileUpload, 'AdobeFileUpload');
        this.logger.markScenario(ScenarioName.AdobeFileUpload, StepName.AdobeFileUploadStart, StatusName.NA, {
            data: {
                adobeUploadId,
            },
        });
        this.logger.logUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.CreateApprovals,
            StepName.FileUploadSelected,
            'click',
            'nav',
            'adobeFileService',
            'button',
            PanelRegion.Modal,
            PanelType.CreateApproval,
            {
                adobeUploadId,
                fileType: file.type,
                fileSize: file.size,
                ApprovalType: ApprovalRequestType.ESign,
            }
        );

        try {
            if (!acceptedFileTypes.includes(file.type)) {
                throw adobe_upload_error_invalid_types;
            }

            const transientDocumentId = await uploadFileEsignGateway(ApprovalRequestType.ESign, file);

            if (!transientDocumentId) {
                throw stringFormat(adobe_upload_error_default, { file: file.name });
            }

            notifier(0, UploadStatus.COMPLETE);
            this.logger.logTraceInfo(LogCategory.AdobeFileUpload, 'Adobe file upload succeeded');
            this.logger.markScenario(ScenarioName.AdobeFileUpload, StepName.AdobeFileUploadEnd, StatusName.NA, {
                data: {
                    adobeUploadId,
                    transientDocumentId,
                },
            });
            this.logger.stopScenario(ScenarioName.AdobeFileUpload, {
                data: {
                    adobeUploadId,
                    transientDocumentId,
                },
            });

            return {
                transientDocumentId,
            };
        } catch (error: any) {
            let message: string = null;

            this.logger.markScenario(ScenarioName.AdobeFileUpload, StepName.AdobeFileUploadFail, StatusName.NA, {
                data: {
                    adobeUploadId,
                },
            });
            this.logger.failScenario(ScenarioName.AdobeFileUpload, {
                data: {
                    adobeUploadId,
                },
            });

            if (error instanceof Error) {
                const code = (error as any).response?.data?.code;

                // If it's an error
                if (code === 'INVALID_ACCESS_TOKEN') {
                    // Adobe access token error
                    message = adobe_upload_error_invalid_token;
                } else if (code === 'UNSUPPORTED_MEDIA_TYPE') {
                    // Adobe unsupported type error
                    message = adobe_upload_error_invalid_types;
                } else if (code === 'LARGE_FILE_SIZE') {
                    // Adobe file size error
                    message = esign_upload_error_file_too_large;
                } else {
                    message = error.message;
                }
            } else if (error && (typeof error === 'string' || error instanceof String)) {
                const errorMessage = error.toString();

                // If it's a message
                if (errorMessage === 'CancelledByUser') {
                    // Upload canceled
                    message = adobe_upload_error_canceled;
                } else if (errorMessage === 'FailedToOpenWindow') {
                    // Authentication window blocked or can't be opened
                    message = adobe_upload_error_open_window_failed;
                } else {
                    message = errorMessage;
                }
            } else {
                // Default error message
                message = stringFormat(adobe_upload_error_default, { file: file.name });
            }

            this.logger.logTraceException(
                LogCategory.AdobeFileUpload,
                message,
                {
                    error: error instanceof Error ? error : undefined,
                    method: 'uploadFile',
                },
                {
                    adobeUploadId,
                }
            );

            throw new Error(message);
        }
    }
}

let adobeFileService: AdobeFileService = null;

export function getAdobeFileService(): AdobeFileService {
    if (!adobeFileService) {
        adobeFileService = new AdobeFileService();
    }
    return adobeFileService;
}
